import axios from 'axios';
import jwtDecode from 'jwt-decode';
import { BatchHttpLink } from '@apollo/client/link/batch-http';
import { ApolloClient, ApolloLink, InMemoryCache } from '@apollo/client';
import { onError } from '@apollo/client/link/error';
import { setContext } from '@apollo/client/link/context';
import { createRateLimitRetryLink } from './rateLimitRetry';

import packageInfo from '../../package.json';
import {
  invalidateSession,
  sessionSnapshot,
  handleAuthenticationFailure,
  ensureSessionForRequest,
  assertRequestSession,
  SessionSnapshot
} from '@global-utils/sessionRenewal';

declare module 'axios' {
  interface AxiosRequestConfig {
    authSession?: SessionSnapshot;
  }
}

const config = {
  host: import.meta.env.VITE_BACKEND_HOST,
  graphqlHost: import.meta.env.VITE_DATA_NEXUS_HOST,
  agaveHost: import.meta.env.VITE_AGAVE_HOST
};

export interface PaginatedResponse<T> {
  count: number;
  next: string | null;
  previous: string | null;
  next_url: string | null;
  previous_url: string | null;
  results: T[];
}

const api = axios.create({
  baseURL: config.host,
  withCredentials: true,
  headers: { 'Content-Type': 'application/json' }
});

export const agaveAPI = axios.create({
  baseURL: config.agaveHost,
  headers: { 'Content-Type': 'application/json' }
});

const batchHttpLink = new BatchHttpLink({
  uri: config.graphqlHost,
  batchMax: 10,
  batchInterval: 20
});

const errorLink = onError(({ graphQLErrors, networkError, operation }) => {
  if (graphQLErrors) {
    for (const err of graphQLErrors) {
      switch (err?.extensions?.code) {
        case 'UNAUTHENTICATED':
          void handleAuthenticationFailure(
            operation.getContext().authSession,
            (networkError as { statusCode?: number })?.statusCode === 401 && err.message === 'Invalid or expired token'
          );
      }
    }
  }

  if (networkError) {
    // biome-ignore lint: necessary console.log
    console.log(`[Network error]: ${networkError}`);
  }
});

const checkCompanyId = (token: string | null) => {
  if (token) {
    const decoded = jwtDecode<{ company_id?: number }>(token);
    const cachedCompanyId = localStorage.getItem('__company_id');

    if (decoded?.company_id && cachedCompanyId && decoded.company_id !== Number(cachedCompanyId)) {
      // This is necessary because a Super Admin can switch companies on app.inbuild, updating the token correctly.
      // However, test-1.inbuild retain the old token with the previous company_id,
      // so we need to invalidate the session in such cases to prevent mismatched company data.
      invalidateSession();
    }
  }
};

const authLink = setContext(async (operation, { headers }) => {
  const snapshot = await ensureSessionForRequest();
  assertRequestSession(snapshot);
  const { operationName } = operation;
  const token = localStorage.getItem('__token');
  const userId = localStorage.getItem('__user_id');
  const userEmail = localStorage.getItem('__user_email');
  const companyId = localStorage.getItem('__company_id');

  checkCompanyId(token);

  return {
    authSession: sessionSnapshot(),
    headers: {
      ...headers,
      authorization: token ? `Bearer ${token}` : '',
      'X-User-ID': userId,
      'X-User-Email': userEmail,
      'X-Company-ID': companyId,
      'X-Dashboard-Version': packageInfo.version ?? 'unknown'
    },
    // The 'operation' as a query param is useful for debugging purposes.
    uri: `${config.graphqlHost}?operation=${operationName}`
  };
});

export const graphqlClient = new ApolloClient({
  // The retry sits above authLink so a retried request gets fresh headers, and below
  // errorLink so only the final failure is reported.
  link: ApolloLink.from([errorLink, createRateLimitRetryLink(), authLink, batchHttpLink]),
  cache: new InMemoryCache()
});

agaveAPI.interceptors.request.use(function (config) {
  const token = localStorage.getItem('__token');

  config.headers.Authorization = token ? `Bearer ${token}` : '';

  return config;
});

api.interceptors.request.use(async function (config) {
  if (!['auth/logout/', 'auth/token/'].includes(config.url ?? '')) {
    const snapshot = await ensureSessionForRequest();
    assertRequestSession(snapshot);
  }
  config.authSession = config.url === 'auth/logout/' ? (config.authSession ?? sessionSnapshot()) : sessionSnapshot();
  const token = localStorage.getItem('__token');

  if (config.url !== 'auth/logout/') {
    config.headers.Authorization = token ? `Bearer ${token}` : '';
  }

  return config;
});

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (error.response?.status === 401) {
      const data = error.response.data;
      const accessExpired =
        data?.code === 'token_not_valid' &&
        Array.isArray(data.messages) &&
        data.messages.some(
          (message) =>
            message.token_class === 'AccessToken' &&
            message.token_type === 'access' &&
            message.message === 'Token is expired'
        );
      await handleAuthenticationFailure(error.config?.authSession, accessExpired);
    }
    return Promise.reject(error);
  }
);

export default api;

export const publicProposalApi = axios.create({
  baseURL: config.host,
  withCredentials: true,
  headers: { 'Content-Type': 'application/json' }
});

export const unauthenticatedApi = axios.create({
  baseURL: config.host,
  headers: { 'Content-Type': 'application/json' }
});
